import { HttpFactoryService, HttpService } from '../../common/services';

export interface ITeam {
  id: number;
  name: string;
  inviteCode: string;
  role: string;
  memberCount: number;
  reviewCount: number;
}

export interface ITeamDetail {
  id: number;
  name: string;
  inviteCode: string;
  reviewCount: number;
  members: ITeamMember[];
}

export interface ITeamMember {
  id: number;
  userId: number;
  email: string;
  displayName: string | null;
  role: string;
  joinedAt: string;
}

class TeamService {
  httpService: HttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createHttpService();
  }

  public async createTeam(name: string) {
    return this.httpService.post<ITeam, { name: string }>('team', { name });
  }

  public async getMyTeams() {
    return this.httpService.get<ITeam[]>('team/my-teams');
  }

  public async getTeam(id: number) {
    return this.httpService.get<ITeamDetail>(`team/${id}`);
  }

  public async getTeamReviews(id: number, activeOnly = false) {
    const url = activeOnly ? `team/${id}/reviews?active=true` : `team/${id}/reviews`;
    return this.httpService.get<any[]>(url);
  }

  public async updateTeam(id: number, name: string) {
    return this.httpService.put<any, { name: string }>(`team/${id}`, {
      name
    });
  }

  public async deleteTeam(id: number) {
    return this.httpService.delete<any>(`team/${id}`);
  }

  public async removeMember(teamId: number, userId: number) {
    return this.httpService.delete<any>(`team/${teamId}/member/${userId}`);
  }

  public async leaveTeam(teamId: number) {
    return this.httpService.post<{ success: boolean }, {}>(`team/${teamId}/leave`, {});
  }

  public async changeMemberRole(teamId: number, userId: number, role: string) {
    return this.httpService.put<any, { role: string }>(
      `team/${teamId}/member/${userId}/role`,
      { role }
    );
  }

  public async joinTeam(inviteCode: string, password?: string) {
    return this.httpService.post<
      { id: number; name: string; alreadyMember: boolean },
      { password?: string }
    >(`team/join/${inviteCode}`, { ...(password ? { password } : {}) });
  }

  public async updateTeamPassword(teamId: number, password?: string) {
    return this.httpService.put<{ hasPassword: boolean }, { password?: string }>(
      `team/${teamId}/password`,
      { password }
    );
  }

  public async sendInvites(teamId: number, emails: string[]) {
    return this.httpService.post<{ sent: number }, { emails: string[] }>(
      `team/${teamId}/send-invites`,
      { emails }
    );
  }

  public async getTeamPassword(teamId: number) {
    return this.httpService.get<{ password: string | null }>(
      `team/${teamId}/password`
    );
  }

  public async getTeamByInviteCode(inviteCode: string) {
    return this.httpService.get<{ id: number; name: string; memberCount: number; hasPassword: boolean }>(
      `team/invite/${inviteCode}`
    );
  }

  public async getTeamInvites(teamId: number) {
    return this.httpService.get<{ email: string; joinedAt: string | null; createdAt: string }[]>(
      `team/${teamId}/invites`
    );
  }

  public async dismissTeamInvites(teamId: number) {
    return this.httpService.delete<{ success: boolean }>(
      `team/${teamId}/invites`
    );
  }
}

const teamService = new TeamService(new HttpFactoryService());
export { teamService };
